import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Alert, FormGroup, FormControl } from 'react-bootstrap';
import { DeviceType, FirmwareType, deviceTypesToFirmwareTypes } from './utils/DeviceType';
import { getOnlineStatusIcon } from './DeviceUtils';
import semver from '../../utils/semver';

export default class UpgradeDeviceDialog extends Component {
  getInitialState() {
    return {
      version: '',
      target: 'selected',
      confirm: false,
    };
  }

  state = this.getInitialState();

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { show } = nextProps;

    if (!show) {
      this.setState(this.getInitialState());
    }
  }

  onChangeVersion = event => this.setState({ version: event.target.value, confirm: false });

  onChangeUpgrade = event => this.setState({ target: event.target.value, confirm: false });

  onChangeConfirm = () => {
    const { confirm } = this.state;

    this.setState({ confirm: !confirm });
  };

  upgrade = () => {
    const { onUpgrade, onUpgradeAll } = this.props;
    const { version, target } = this.state;

    if (target === 'selected') {
      onUpgrade(version);
    } else {
      onUpgradeAll(version);
    }
  };

  getVersionsThatAvailableForEveryDevice = (devices, versions) => {
    // e.g. deviceTypes = ['', 'android_allsee_batterypowered_ekd08', 'android_allsee_dualscreen_ekd08', 'android_apc380', 'android_ekd08', 'android_m12', 'android_mk808', 'android_other', 'android_tpv', 'android_tpv_4150'];
    const deviceTypes = [...new Set(devices.map(item => item.device_type))].sort(
      // case-insensitive, numeric-aware (natural) sort
      (a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' }),
    );
    // e.g. firmwareTypes = ['android', 'tpv_3650', 'tpv_4050', 'tpv_4051', 'tpv_4150', 'tpv_4550', 'tpv_4650', 'tpv_crd50']
    const firmwareTypes = deviceTypesToFirmwareTypes(deviceTypes);
    // e.g. availableVersions = ['3.3.4', '3.3.5'] these are the available versions for EVERY device
    let availableVersions = versions
      .filter(item => firmwareTypes.every(type => item.firmware_types.includes(type)))
      .map(item => item.version)
      .filter(semver.valid)
      .sort((b, a) => semver.compare(a, b)); // sort in descending order (newest version first)
    // there is a device with a device_type that is missing from the DeviceType object - this results in FirmwareType.UNKNOWN
    // e.g. device.device_type = 'some_unknown_device_type'
    const hasUnknown = firmwareTypes.includes(FirmwareType.UNKNOWN);
    // legacy Windows device version starts with 'win' e.g. 'win2.8.5'
    // e.g device.version = 'win2.8.5'
    const hasLegacyWindows = devices.some(item => item.version?.startsWith('win'));
    // HTML Player based players do not return device_type and firmware_version prior 1.6.4
    const hasHtml = devices
      .filter(item => !item.device_type && !item.firmware_version)
      .filter(item => semver.valid(item.version))
      .some(item => semver.compare(item.version, '1.6.4') === -1);
    const samsungTizenTypes = [
      DeviceType.SAMSUNG_TIZEN_4,
      DeviceType.SAMSUNG_TIZEN_5,
      DeviceType.SAMSUNG_TIZEN_7,
    ];
    const hasDeprecatedLegacySamsung = devices.some(
      item =>
        samsungTizenTypes.includes(item.device_type) &&
        semver.valid(item.version) &&
        semver.compare(item.version, '1.6.5') === -1,
    );
    // Android Players --------------------------------------------------------------------------------
    const hasWedge = devices.some(item => item.device_type === DeviceType.EKD08_WEDGE); // up to 3.3.3
    const hasAllSeeDualScreen = deviceTypes.includes(DeviceType.ALLSEE_DUALSCREEN_EKD08); // from 3.3.5
    const hasRefeeBa37b = deviceTypes.includes(DeviceType.REFEE_BA37B); // from 3.3.7
    const hasVestel400vs = deviceTypes.includes(DeviceType.VESTEL_400VS); // from 3.3.7
    const hasVestelOther = deviceTypes.includes(DeviceType.ANDROID_OTHER_VESTEL); // from 3.3.7
    const hasX6 = deviceTypes.includes(DeviceType.X96_X6); // from 3.3.7
    const hasRefeeA55fd = deviceTypes.includes(DeviceType.REFEE_A55FD); // from 3.3.13
    const hasPhilips4050 = deviceTypes.includes(DeviceType.PHILIPS_4050); // from 3.3.8
    const hasLamasaDualScreen = deviceTypes.includes(DeviceType.LAMASA_DUALSCREEN); // from 3.3.14
    const hasLamasaDualScreenS3568 = deviceTypes.includes(DeviceType.LAMASA_DUALSCREEN_S3568); // from 3.3.24
    const hasLedStudio339a = deviceTypes.includes(DeviceType.LEDSTUDIO_339A); // from 3.3.16
    const hasHisenseDm = deviceTypes.includes(DeviceType.HISENSE_DM); // from 3.3.19
    const hasS35 = deviceTypes.includes(DeviceType.S35); // from 3.3.19
    const hasAllSee = deviceTypes.includes(DeviceType.ALLSEE); // from 3.3.20
    const hasDauha = deviceTypes.includes(DeviceType.DAHUA_LDMA_T982); // from 3.3.20
    const hasKmsBeten = deviceTypes.includes(DeviceType.KMS_BETEN); // from 3.3.21
    const hasPhilips = deviceTypes.includes(DeviceType.PHILIPS); // from 3.3.26 - generic Philips devices (unknown models), it uses the standard apk
    const hasFusionH76 = deviceTypes.includes(DeviceType.FUSION_H76); // from 3.4.1
    const hasAllSeeGv3568 = deviceTypes.includes(DeviceType.ALLSEE_GV3568); // from 3.4.3
    // HTML Player based players -----------------------------------------------
    const hasLinux = deviceTypes.includes(DeviceType.LINUX); // from 1.6.4
    const hasWindows = deviceTypes.includes(DeviceType.WINDOWS); // from 1.6.4
    const hasSamsungTizen4 = deviceTypes.includes(DeviceType.SAMSUNG_TIZEN_4); // from 1.6.5, 1.6.4 used the deprecated generic Samsung path
    const hasSamsungTizen5 = deviceTypes.includes(DeviceType.SAMSUNG_TIZEN_5); // from 1.6.5
    const hasSamsungTizen7 = deviceTypes.includes(DeviceType.SAMSUNG_TIZEN_7); // from 1.6.5, 1.6.4 used the deprecated generic Samsung path

    // non-default apk-s:
    // (no need to check for first supported version, like we do it for default apk based players)
    // PHILIPS_3650:     supported from 3.3.15,   apk: CmsClient_3.3.15.3650Q.apk
    // PHILIPS_4050_ZIP: supported from 2.10.5L4, apk: update_2.10.5L4.zip
    // PHILIPS_4050:     supported from 3.3.8,    apk: CmsClient_3.3.8.apk (default apk)
    // PHILIPS_4051:     supported from 3.3.4R3,  apk: CmsClient_3.3.5.4051D.apk
    // PHILIPS_4150:     supported from 3.3.0,    apk: CmsClient_3.3.0.4150D.apk
    // PHILIPS_4550:     supported from 3.3.5,    apk: CmsClient_3.3.5.4550D.apk
    // PHILIPS_4650:     supported from 3.3.14,   apk: CmsClient_3.3.14.4650D.apk
    // PHILIPS_CRD50:    supported from 3.3.14,   apk: CmsClient_3.3.14.CRD50.apk
    // ALLSEE_BATTERYPOWERED_EKD08:
    //   1.              supported exactly 3.3.5, apk: CmsClient_3.3.5.allsee_batterypowered.apk (one version apk)
    //   2.              not yet supported        apk: CmsClient_3.?.?.allsee.apk (no such apks)
    // ALLSEE:           supported from 3.4.3,    apk: CmsClient_3.4.3.AllSee_General.apk (below 3.4.3: default apk)

    if (hasUnknown || hasLegacyWindows || hasHtml || hasDeprecatedLegacySamsung) {
      // prevent the upgrade, unknown device or legacy Windows Player cannot be updated in Director,
      // HTML Player does not support upgrade yet, or the Samsung player is on the deprecated generic path
      availableVersions = [];
    }

    if (hasWedge) {
      // supported up to 3.3.3, using default apk (they are only tested up to 3.3.3)
      // we also added allsee_dualscreen support in 3.3.5 and wedge devices are no longer supported because of that
      // wedge and allsee_dualscreen devices are both using the default apk and they have rk3288 chipset
      // because no model numbers are provided in their API, wedge and allsee_dualscreen devices are indistinguishable
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.4') === -1);
    }

    if (hasAllSeeDualScreen) {
      // supported from 3.3.5, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.5') > -1);
    }

    if (hasRefeeBa37b || hasVestel400vs || hasVestelOther || hasX6) {
      // supported from 3.3.7, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.7') > -1);
    }

    if (hasPhilips4050) {
      // supported from 3.3.8, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.8') > -1);
    }

    if (hasRefeeA55fd) {
      // supported from 3.3.13, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.13') > -1);
    }

    if (hasLamasaDualScreen) {
      // supported from 3.3.14, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.14') > -1);
    }

    if (hasLedStudio339a) {
      // supported from 3.3.16, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.16') > -1);
    }

    if (hasHisenseDm || hasS35) {
      // supported from 3.3.19, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.19') > -1);
    }

    if (hasAllSee || hasDauha) {
      // supported from 3.3.20, using default apk, android_allsee switches to its own apk from 3.4.3 (see the list above)
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.20') > -1);
    }

    if (hasKmsBeten) {
      // supported from 3.3.21, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.21') > -1);
    }

    if (hasLamasaDualScreenS3568) {
      // supported from 3.3.24, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.24') > -1);
    }

    if (hasPhilips) {
      // supported from 3.3.26, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.3.26') > -1);
    }

    if (hasFusionH76) {
      // supported from 3.4.1, using default apk
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.4.1') > -1);
    }

    if (hasAllSeeGv3568) {
      // supported from 3.4.3, using default apk (Android 14, different chipset to android_allsee, no firmware issue)
      availableVersions = availableVersions.filter(item => semver.compare(item, '3.4.3') > -1);
    }

    // HTML Player based players -----------------------------------------------

    if (hasLinux || hasWindows) {
      // supported from 1.6.4
      availableVersions = availableVersions.filter(item => semver.compare(item, '1.6.4') > -1);
    }

    if (hasSamsungTizen4 || hasSamsungTizen5 || hasSamsungTizen7) {
      // supported from 1.6.5, pre-1.6.5 Samsung builds used the deprecated generic Samsung path
      availableVersions = availableVersions.filter(item => semver.compare(item, '1.6.5') > -1);
    }

    return availableVersions;
  };

  getVersionOptions = (devices, availableVersions) => {
    const errorMessage =
      devices.length > 1
        ? 'No compatible player versions for the selected devices - try to select fewer devices.'
        : 'No player versions available for this device.';
    const optionMessage = availableVersions.length ? 'Select a player version' : errorMessage;
    const options = availableVersions.map((item, index) => {
      const optionsKey = `${item}-${index}`;

      return <option key={optionsKey}>{item}</option>;
    });

    options.unshift(
      <option key={-1} value="" disabled>
        {optionMessage}
      </option>,
    );

    return options;
  };

  getDevices = () => {
    const { devices } = this.props;
    const rows = devices.map((item, index) => {
      const trKey = `${item.name}-${index}`;

      return (
        <tr key={trKey}>
          <td>{index + 1}.</td>
          <td>{getOnlineStatusIcon(item)}</td>
          <td>
            <code className="text-sunrise bg-orange-50">{item.name}</code>
          </td>
          <td>{item.description}</td>
          <td>{item.version || <small className="text-light">N/A</small>}</td>
          <td>{item.device_type || <small className="text-light">N/A</small>}</td>
          <td>{item.firmware_version || <small className="text-light">N/A</small>}</td>
        </tr>
      );
    });

    return (
      <table className="table table-striped table-device-upgrade">
        <thead>
          <tr>
            <th />
            <th />
            <th>Device ID</th>
            <th>Device Name</th>
            <th>Player Version</th>
            <th>Device Type</th>
            <th>Firmware</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    );
  };

  render() {
    const { show, onHide, devices, versions, total } = this.props;
    const { version, target, confirm } = this.state;
    const plural = devices.length > 1 ? 's' : '';
    const availableVersions = this.getVersionsThatAvailableForEveryDevice(devices, versions);

    return (
      <Modal
        data-test="modal-upgradedevice"
        dialogClassName="device-dialog width-auto"
        backdrop="static"
        show={show}
        onHide={onHide}
      >
        <Modal.Header>
          <Modal.Title data-test="modal-upgradedevice-title">Upgrade Device{plural}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <label>
              SignStix Player version
              <FormControl componentClass="select" value={version} onChange={this.onChangeVersion}>
                {this.getVersionOptions(devices, availableVersions)}
              </FormControl>
            </label>
          </FormGroup>
          {total && (
            <FormGroup>
              <label>Device{plural} to upgrade</label>
              <FormControl componentClass="select" value={target} onChange={this.onChangeUpgrade}>
                <option value="selected">
                  Selected ({devices.length} device{plural})
                </option>
                <option value="all">All Compatible Devices</option>
              </FormControl>
            </FormGroup>
          )}
          {target === 'all' && (
            <div>
              You are going to upgrade <b>ALL</b> of your compatible devices.
              <br />
              <br />
            </div>
          )}
          {target === 'selected' && (
            <div>
              <p>
                {devices.length} device{plural} to upgrade:
              </p>
              {this.getDevices()}
            </div>
          )}
          {version && (
            <Alert bsStyle="warning">
              <label>
                <input
                  data-test="modal-upgradedevice-checkbox-confirm"
                  type="checkbox"
                  checked={confirm}
                  onChange={this.onChangeConfirm}
                />{' '}
                I want to upgrade{' '}
                {target === 'all' ? 'all compatible Devices' : `${devices.length} Device${plural}`}{' '}
                to Player version {version}.
              </label>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={onHide}>
            Cancel
          </Button>
          <Button
            data-test="modal-upgradedevice-button-upgrade"
            className="btn-add"
            bsStyle="primary"
            onClick={this.upgrade}
            disabled={!confirm || !version}
          >
            Upgrade Device{plural}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

UpgradeDeviceDialog.propTypes = {
  devices: PropTypes.array.isRequired,
  total: PropTypes.number,
  versions: PropTypes.array.isRequired,
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
  onUpgradeAll: PropTypes.func.isRequired,
};
